import { ReactNode, useMemo } from 'react';
import {
  Asset,
  EmptyFilesUpload,
  EmptyFilesUploadError,
  FacialAuthenticationStatus,
  TrackingFamily,
} from '@facephi/inphinite-common';
import { Icon, Label } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { ImageLoad } from './ImageLoad';
import { FacialValidationState, infoFacial, tInfo } from './model';
import { OperationsCardIcon } from './operationsCards/Styles';
import {
  FacialCheckGrid,
  FacialContainer,
  FacialResultIconWrapper,
  InfoContainer,
} from './Styles';
import { useOperations } from '../../providers';
import {
  CUSTOMER_PROVIDED_IMAGE,
  DOCUMENT_FACIAL_CAPTURE,
  INTERNAL_PROVIDERS,
  NFC_PHOTO,
  SELFIE,
  availableFeatureFlags,
} from '../../state/constants';

type Props = {
  state?: FacialValidationState[];
  assets?: Asset[];
  loading?: boolean;
  family?: string;
};

const ImageSkeleton = () => (
  <div>
    <Skeleton width={120} height={120} borderRadius={8} />
    <div style={{ marginTop: '0.8rem' }}>
      <Skeleton width="50%" height={18} borderRadius={8} />
    </div>
  </div>
);

export const SelfieValidations = ({
  state,
  assets = [],
  loading,
  family,
}: Props) => {
  const { t } = useTranslation();
  const [{ authStatus, source, id, similarity }] = state || [{}];
  const { token, activeFlags } = useOperations();
  const emptyState = !state || !state.length;

  const documentFacialCapture = useMemo(
    () =>
      assets.length
        ? assets.find(({ type }) =>
            source === INTERNAL_PROVIDERS.nfcMobile
              ? type === NFC_PHOTO
              : type === DOCUMENT_FACIAL_CAPTURE ||
                type === CUSTOMER_PROVIDED_IMAGE,
          )
        : undefined,
    [assets, source],
  );

  const selfie = useMemo(
    () =>
      assets.length ? assets.find(({ type }) => type === SELFIE) : undefined,
    [assets],
  );

  const similarityResult: string = useMemo(
    () =>
      activeFlags
        ? activeFlags.includes(availableFeatureFlags.similarity)
          ? ` (${
              (similarity && similarity > 0
                ? (similarity * 100).toPrecision(4)
                : 0
              ).toString() || ''
            } %)`
          : ''
        : '',
    [similarity],
  );

  const getLoadingImages = () => (
    <>
      <ImageSkeleton />
      <Skeleton width={40} height={40} circle />
      <ImageSkeleton />
    </>
  );

  const info: tInfo | undefined = useMemo(() => {
    if (!authStatus && family === TrackingFamily.AUTHENTICATION)
      return infoFacial['authOK'];
    if (emptyState) return undefined;
    if (!authStatus) return infoFacial['notAble'];
    const type =
      authStatus === FacialAuthenticationStatus.positive
        ? 'match'
        : authStatus === FacialAuthenticationStatus.negative
          ? 'notMatch'
          : 'notAble';
    return infoFacial[type];
  }, [state]);

  const isInternalProvider = useMemo(
    () => source && Object.values(INTERNAL_PROVIDERS).includes(source),
    [source],
  );

  const getEmptyImageItem = (isSecondImage?: boolean): ReactNode =>
    !isInternalProvider && isSecondImage ? (
      <OperationsCardIcon $bgColor="grey100" $remSize="8.9rem">
        <Icon
          iconName={'CameraSlash'}
          size="32"
          color="white"
          weight={'fill'}
        />
      </OperationsCardIcon>
    ) : authStatus === FacialAuthenticationStatus.positive ? (
      <EmptyFilesUpload />
    ) : family === TrackingFamily.AUTHENTICATION ? null : (
      <EmptyFilesUploadError />
    );

  const getImages = () => (
    <>
      <FacialContainer flexDirection="column" rowGap="0.8">
        {selfie ? (
          <ImageLoad asset={selfie} token={token} />
        ) : (
          getEmptyImageItem(!selfie)
        )}
      </FacialContainer>

      {info && (
        <OperationsCardIcon
          $bgColor={info.backgroundColor}
          $remSize="6rem"
          key={`provider-${id}`}
          id="facial-match-icon"
          $borderRadius="1.7rem"
          className="facial-match-animated"
        >
          <Icon
            iconName={info.iconName}
            size={info.size}
            color={info?.iconColor}
            weight={info.weight}
          />
        </OperationsCardIcon>
      )}
      {!emptyState && (
        <FacialContainer
          flexDirection="column"
          rowGap="0.8"
          className="facial-match-animated"
          key={`image-${documentFacialCapture?.type}`}
        >
          {family !== TrackingFamily.AUTHENTICATION && documentFacialCapture ? (
            <ImageLoad asset={documentFacialCapture} token={token} />
          ) : (
            !emptyState && getEmptyImageItem()
          )}
        </FacialContainer>
      )}
    </>
  );

  return (
    <>
      <FacialCheckGrid
        alignItems="center"
        columnGap="0.8"
        justifyContent={emptyState ? 'center' : 'space-between'}
        gridTemplateColumns={emptyState ? '12rem' : '12rem auto 12rem'}
        gridTemplateRows="10rem auto"
        rowGap="1.2"
      >
        {loading ? getLoadingImages() : getImages()}
        {!loading && (
          <>
            <Label className="asset-text" fontSize="12">
              {t('SELFIE')}
            </Label>
            {family !== TrackingFamily.AUTHENTICATION && !emptyState && (
              <Label
                fontSize="12"
                className="asset-text facial-match-animated"
                id="document-text"
                key={`label-${documentFacialCapture?.type}`}
              >
                {t(documentFacialCapture?.type || '')}
              </Label>
            )}
          </>
        )}
      </FacialCheckGrid>
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <Skeleton width="80%" height={18} borderRadius={8} />
        </div>
      ) : (
        info && (
          <InfoContainer
            $infoBackground={info?.backgroundColor}
            $infoColor={info?.iconColor}
            rowGap="0.8"
            flexDirection="row"
            alignItems="center"
            key={`provider-${id}`}
          >
            <FacialResultIconWrapper>
              <Icon
                iconName="ChatsCircle"
                color={info?.iconColor}
                size="32"
                weight="thin"
              />
            </FacialResultIconWrapper>
            <Label fontSize="12" semibold>
              {family === TrackingFamily.AUTHENTICATION
                ? `${t(
                    info?.message_authentication as string,
                  )} ${similarityResult}`
                : `${t(info?.message as string)} ${similarityResult}`}
            </Label>
          </InfoContainer>
        )
      )}
    </>
  );
};
